import { useId, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { getMatchingDiscoveryExchanges } from '@/lib/screenerDiscovery'
import { cn } from '@/lib/utils'
import type { ScreenerDiscoveryExchangeDetail, ScreenerDiscoveryFilters, ScreenerDiscoveryItem } from '@/types'

export const DISCOVERY_COLUMNS = 'grid-cols-[minmax(72px,1.1fr)_minmax(68px,1fr)_minmax(0,0.9fr)_minmax(0,0.75fr)_minmax(0,0.9fr)]'
const historyDateFormat = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' })

function PriceCell({ value }: { value?: number | null }) {
  const valid = value != null && Number.isFinite(value) && value > 0
  const price = !valid ? '—' : `$${value < 0.00001
    ? value.toExponential(2)
    : value.toLocaleString('en-US', value < 1
      ? { maximumSignificantDigits: 4 }
      : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return <div className="truncate text-right font-mono text-[10px] text-slate-400" title={valid ? `$${value.toLocaleString('en-US', { maximumSignificantDigits: 15 })}` : 'Price unavailable'}>{price}</div>
}

function compactNumber(value: number | null | undefined, currency = false): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const sign = value < 0 ? '-' : ''
  const absolute = Math.abs(value)
  const prefix = currency ? '$' : ''
  if (absolute >= 1_000_000_000) return `${sign}${prefix}${(absolute / 1_000_000_000).toFixed(2)}B`
  if (absolute >= 1_000_000) return `${sign}${prefix}${(absolute / 1_000_000).toFixed(2)}M`
  if (absolute >= 1_000) return `${sign}${prefix}${(absolute / 1_000).toFixed(1)}K`
  return `${sign}${prefix}${absolute.toFixed(absolute >= 100 ? 0 : 2)}`
}

function signedUsd(value: number | null | undefined): string {
  return `${value != null && value > 0 ? '+' : ''}${compactNumber(value, true)}`
}

function signedPercent(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(2)}%`
}

function changeColor(value: number | null | undefined): string {
  if (value == null || value === 0) return 'text-slate-500'
  return value > 0 ? 'text-teal-400' : 'text-rose-400'
}

function ageLabel(timestamp: number): string {
  const age = Math.max(0, Math.floor(Date.now() / 1000 - timestamp))
  if (age < 60) return `${age}s ago`
  if (age < 3600) return `${Math.floor(age / 60)}m ago`
  return `${Math.floor(age / 3600)}h ago`
}

function comparisonHistory(detail: ScreenerDiscoveryExchangeDetail): string {
  const { previous_ts: previous, latest_ts: latest } = detail
  if (previous == null || !Number.isFinite(previous) || !Number.isFinite(latest) || previous >= latest) return `${detail.exchange}: comparison unavailable`
  const elapsed = latest - previous
  const duration = elapsed < 60 ? `${Math.floor(elapsed)}s` : elapsed < 3600 ? `${Math.floor(elapsed / 60)}m` : `${(elapsed / 3600).toFixed(1)}h`
  const format = (timestamp: number) => historyDateFormat.format(timestamp * 1000)
  return `${detail.exchange}: ${duration} comparison, ${format(previous)} → ${format(latest)} UTC`
}

export function DiscoveryResultRow({ item, filters, active, onSelect }: { item: ScreenerDiscoveryItem; filters: ScreenerDiscoveryFilters; active: boolean; onSelect: () => void }) {
  const [expanded, setExpanded] = useState(false)
  const detailId = useId()
  const perExchange = filters.filterScope === 'exchange'
  const matching = getMatchingDiscoveryExchanges(item, filters)
  const period = filters.changePeriod ?? '24h'
  const pctKey = `oi_change_pct_${period}` as const
  const usdKey = `oi_change_usd_${period}` as const
  const strongest = matching
    .filter((detail) => Number.isFinite(detail[usdKey]))
    .sort((left, right) => Math.abs(right[usdKey] ?? 0) - Math.abs(left[usdKey] ?? 0))[0]
  const matchingNames = new Set(matching.map((detail) => detail.exchange))
  const selected = new Set(filters.exchanges)
  const details = (item.exchange_details || []).filter((detail) => selected.has(detail.exchange))
  const historyHelp = `${period} OI change. Exchanges with shorter history use their earliest available datapoint.`
  const historyTitle = [historyHelp, ...details.map(comparisonHistory)].join('\n')

  return (
    <div className={cn('border-b border-slate-800/65', active && 'bg-teal-400/10')}>
      <button type="button" onClick={onSelect} aria-label={`Open ${item.symbol} in screener`}
        className={cn('grid w-full items-center gap-1.5 px-3 pb-2 pt-2.5 text-left transition-colors hover:bg-slate-900/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-teal-400', DISCOVERY_COLUMNS)}>
        <div className="min-w-0">
          <div className={cn('truncate text-xs font-semibold', active ? 'text-teal-300' : 'text-slate-100')}>{item.symbol}</div>
          <div className="mt-0.5 flex flex-wrap gap-x-1 text-[9px] text-slate-600"><span>{item.exchanges.length} venue{item.exchanges.length === 1 ? '' : 's'}</span><span>· {ageLabel(item.latest_ts)}</span></div>
        </div>
        <PriceCell value={item.last_price} />
        <div className="truncate text-right font-mono text-[10px] text-slate-300" title={compactNumber(item.oi_usd, true)}>{compactNumber(item.oi_usd, true)}</div>
        <div className={cn('truncate text-right font-mono text-[10px] font-medium', changeColor(item[pctKey]))} title={`${signedPercent(item[pctKey])}\n${historyTitle}`}>{signedPercent(item[pctKey])}</div>
        <div className={cn('truncate text-right font-mono text-[10px] font-medium', changeColor(item[usdKey]))} title={`${signedUsd(item[usdKey])}\n${historyTitle}`}>{signedUsd(item[usdKey])}</div>
      </button>
      {details.length > 0 && (
        <button type="button" aria-expanded={expanded} aria-controls={detailId} aria-label={`${expanded ? 'Hide' : 'Show'} ${item.symbol} exchange breakdown`} onClick={() => setExpanded(!expanded)}
          className="flex w-full items-center justify-between gap-2 px-3 pb-2 text-left text-[10px] text-slate-500 transition-colors hover:text-slate-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-teal-400">
          {perExchange && strongest ? (
            <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="capitalize text-slate-300" title={`Largest ${period} OI change in USD by magnitude among matching exchanges`}>{strongest.exchange}</span>
              <span className={cn('font-mono', changeColor(strongest[usdKey]))} title={`${historyHelp}\n${comparisonHistory(strongest)}`}>{signedUsd(strongest[usdKey])}</span>
              <span className={cn('font-mono', changeColor(strongest[pctKey]))} title={`${historyHelp}\n${comparisonHistory(strongest)}`}>{signedPercent(strongest[pctKey])}</span>
              <span>· {matching.length} match{matching.length === 1 ? '' : 'es'}</span>
            </span>
          ) : <span>Exchange breakdown</span>}
          {expanded ? <ChevronUp className="h-3 w-3 shrink-0" /> : <ChevronDown className="h-3 w-3 shrink-0" />}
        </button>
      )}
      {expanded && details.length > 0 && (
        <div id={detailId} className="border-t border-slate-800/60 bg-slate-950/35 px-3 py-2">
          <div className={cn('mb-1.5 grid gap-1.5 text-[9px] font-medium text-slate-600', DISCOVERY_COLUMNS)}><span>Exchange</span><span className="text-right">Price</span><span className="text-right">OI $</span><span className="text-right">{period} %</span><span className="text-right">{period} Δ $</span></div>
          {details.map((detail) => <div key={detail.exchange} className={cn('grid items-center gap-1.5 py-1.5 text-[10px]', DISCOVERY_COLUMNS)}>
            <div className="flex min-w-0 items-center gap-1.5"><span className="truncate capitalize text-slate-400">{detail.exchange}</span>{perExchange && matchingNames.has(detail.exchange) && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-teal-400" title="Meets all rules" aria-label="Meets all rules" />}</div>
            <PriceCell value={detail.last_price} />
            <span className="truncate text-right font-mono text-slate-400" title={compactNumber(detail.oi_usd, true)}>{compactNumber(detail.oi_usd, true)}</span>
            <span className={cn('truncate text-right font-mono', changeColor(detail[pctKey]))} title={`${signedPercent(detail[pctKey])}\n${historyHelp}\n${comparisonHistory(detail)}`}>{signedPercent(detail[pctKey])}</span>
            <span className={cn('truncate text-right font-mono', changeColor(detail[usdKey]))} title={`${signedUsd(detail[usdKey])}\n${historyHelp}\n${comparisonHistory(detail)}`}>{signedUsd(detail[usdKey])}</span>
          </div>)}
          {perExchange && <p className="mt-1 flex items-center gap-1.5 text-[9px] text-slate-600"><span className="h-1.5 w-1.5 rounded-full bg-teal-400" />Meets all rules on this exchange</p>}
        </div>
      )}
    </div>
  )
}
